import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { useEmailCampaign } from '@/hooks/useEmailCampaigns'
import {
  useCampaignProspects,
  useExcludeFromCampaign,
} from '@/hooks/useCampaignProspects'
import {
  CAMPAIGN_PROSPECT_STATUS_LABELS,
  CAMPAIGN_PROSPECT_STATUS_COLORS,
} from '@/types/emailCampaigns'
import { X } from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

interface Props {
  campaignId: string
  onClose: () => void
}

export default function CampaignDetail({ campaignId, onClose }: Props) {
  const { data: campaign } = useEmailCampaign(campaignId)
  const { data: rows, isLoading } = useCampaignProspects(campaignId)
  const exclude = useExcludeFromCampaign()

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <Card padding="none" className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-brown-100">
          <div>
            <h3 className="text-base font-medium text-brown-900">
              {campaign?.name ?? '…'}
            </h3>
            {campaign && (
              <p className="text-xs text-brown-500">
                {campaign.sent_count}/{campaign.total_count} envoyés,{' '}
                {campaign.failed_count} échec(s)
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : !rows?.length ? (
            <p className="text-sm text-brown-500">Aucun destinataire.</p>
          ) : (
            <table className="w-full">
              <thead className="text-xs text-brown-500 uppercase">
                <tr>
                  <th className="px-2 py-2 text-left">Destinataire</th>
                  <th className="px-2 py-2 text-left">Statut</th>
                  <th className="px-2 py-2 text-left">Envoyé</th>
                  <th className="px-2 py-2 text-left">Erreur</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-brown-100">
                    <td className="px-2 py-2 text-sm text-brown-900">{r.email_to}</td>
                    <td className="px-2 py-2">
                      <Badge variant={CAMPAIGN_PROSPECT_STATUS_COLORS[r.status]}>
                        {CAMPAIGN_PROSPECT_STATUS_LABELS[r.status]}
                      </Badge>
                    </td>
                    <td className="px-2 py-2 text-xs text-brown-500">
                      {r.sent_at
                        ? format(new Date(r.sent_at), 'd MMM HH:mm', { locale: fr })
                        : '-'}
                    </td>
                    <td className="px-2 py-2 text-xs text-red-600 max-w-[12rem] truncate">
                      {r.error_message ?? ''}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.status === 'queued' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => exclude.mutate(r.id)}
                        >
                          Exclure
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  )
}
